import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Loader2 } from 'lucide-react';

let activeVideoId = null;

const VideoThumbnail = ({ 
  videoUrl, 
  fallbackImage, 
  alt = "Video thumbnail",
  className = "",
  showPlayButton = true,
  captureTime = null
}) => {
  const videoRef = useRef(null);
  const instanceId = useRef(`vt-${Math.random().toString(36).slice(2)}`);
  const [thumbnail, setThumbnail] = useState(fallbackImage || null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [loading, setLoading] = useState(!fallbackImage && !!videoUrl);

  // Generate thumbnail from video
  useEffect(() => {
    if (!videoUrl || fallbackImage) { setLoading(false); return; }

    const video = document.createElement('video');
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;

    const timeout = setTimeout(() => {
      setLoading(false);
      video.src = '';
    }, 4000);

    video.onloadeddata = () => {
      const seekTime = captureTime 
        ? video.duration * captureTime 
        : Math.min(2, video.duration * 0.9);
      video.currentTime = Math.max(0.1, seekTime);
    };

    video.onseeked = () => {
      clearTimeout(timeout);
      try {
        const canvas = document.createElement('canvas');
        canvas.width = video.videoWidth || 360;
        canvas.height = video.videoHeight || 640;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL('image/jpeg', 0.7);
        if (dataUrl && dataUrl.length > 100) {
          setThumbnail(dataUrl);
        }
      } catch (e) {
        void 0;
      }
      setLoading(false);
      video.src = '';
    };

    video.onerror = () => {
      clearTimeout(timeout);
      setLoading(false);
      video.src = '';
    };

    video.src = videoUrl;
    video.load();

    return () => {
      clearTimeout(timeout);
      video.src = '';
    };
  }, [videoUrl, fallbackImage, captureTime]);

  // Listen for other videos starting — pause this one
  const handleOtherVideoPlay = useCallback((e) => {
    if (e.detail !== instanceId.current && isPlaying && videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
    }
  }, [isPlaying]);

  useEffect(() => {
    window.addEventListener('video-play', handleOtherVideoPlay);
    return () => window.removeEventListener('video-play', handleOtherVideoPlay);
  }, [handleOtherVideoPlay]);

  const handlePlay = () => {
    // Notify all other videos to stop
    window.dispatchEvent(new CustomEvent('video-play', { detail: instanceId.current }));
    activeVideoId = instanceId.current;
    setIsPlaying(true);
  };

  if (isPlaying) {
    return (
      <video
        ref={videoRef}
        src={videoUrl}
        controls
        autoPlay
        playsInline
        className={`w-full h-full object-cover ${className}`}
        onPause={() => {
          if (activeVideoId !== instanceId.current) {
            setIsPlaying(false);
          }
        }}
        onEnded={() => setIsPlaying(false)}
      />
    );
  }

  return (
    <div 
      className={`relative cursor-pointer group ${className}`}
      onClick={handlePlay}
    >
      {loading ? (
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex flex-col items-center justify-center gap-3">
          <Loader2 size={32} className="text-purple-400 animate-spin" />
          <span className="text-xs text-gray-400">Loading preview...</span>
        </div>
      ) : thumbnail ? (
        <img 
          src={thumbnail} 
          alt={alt}
          className="w-full h-full object-cover"
          loading="lazy"
          decoding="async"
        />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-gray-800 to-gray-900 flex items-center justify-center">
          <Play size={48} className="text-white/60" />
        </div>
      )}
      
      {showPlayButton && !loading && (
        <div className="absolute inset-0 bg-black/20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg transform group-hover:scale-110 transition-transform">
            <Play size={22} className="text-purple-600 ml-1" />
          </div>
        </div>
      )}
    </div>
  );
};

export default VideoThumbnail;
